import React, { useEffect, useState } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import OTPInput from "react-otp-input";
import LoadingButton from "components/shared/LoadingButton";
import styles from "layout/Auth/Auth.module.scss";
import { useResendLoginOtpMutation, useVerifyLoginOtpMutation } from "store/services/sso";

const deadline = (seconds) => Date.now() + Math.max(0, Number(seconds) || 0) * 1000;
const remaining = (until, now) => Math.max(0, Math.ceil((until - now) / 1000));

const LoginOtp = ({ challenge, onRestart }) => {
  const [currentChallenge, setCurrentChallenge] = useState(challenge);
  const [otp, setOtp] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [expiresAt, setExpiresAt] = useState(() => deadline(challenge.expires_in));
  const [resendAt, setResendAt] = useState(() => deadline(challenge.resend_after));
  const [now, setNow] = useState(Date.now());
  const [verifyOtp, { isLoading: verifying }] = useVerifyLoginOtpMutation();
  const [resendOtp, { isLoading: resending }] = useResendLoginOtpMutation();
  const expiresIn = remaining(expiresAt, now);
  const resendIn = remaining(resendAt, now);
  const busy = verifying || resending;

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  const handleError = (err) => {
    const response = err?.data;
    const text = response?.message || "Unable to complete verification. Please try again.";
    if (response?.data?.restart_login) {
      onRestart(text);
      return;
    }
    const attempts = response?.data?.attempts_remaining;
    setError(`${text}${typeof attempts === "number" ? ` Attempts remaining: ${attempts}.` : ""}`);
  };

  const handleVerify = async (event) => {
    event.preventDefault();
    if (busy || Date.now() >= expiresAt) return;
    setError("");
    setMessage("");
    if (!/^\d{6}$/.test(otp)) {
      setError("Please enter the six-digit OTP.");
      return;
    }
    try {
      await verifyOtp({ challenge_id: currentChallenge.challenge_id, otp }).unwrap();
    } catch (err) {
      handleError(err);
    }
  };

  const handleResend = async () => {
    if (busy || Date.now() < resendAt || Date.now() >= expiresAt) return;
    setError("");
    setMessage("");
    try {
      const data = await resendOtp({ challenge_id: currentChallenge.challenge_id }).unwrap();
      setCurrentChallenge({ ...currentChallenge, ...data });
      setExpiresAt(deadline(data.expires_in));
      setResendAt(deadline(data.resend_after));
      setNow(Date.now());
      setOtp("");
      setMessage("OTP sent to your email address.");
    } catch (err) {
      handleError(err);
    }
  };

  return (
    <Form className={`${styles.form} pt-3`} onSubmit={handleVerify}>
      <div className="text-center mb-3">
        {/* <h4 className="fw-bold">Enter OTP</h4> */}
        <p className="text-muted">Enter the six-digit code sent to {currentChallenge.email_masked || "your email address"}.</p>
      </div>
      <Row className="justify-content-center">
        <Col lg="12">
          <fieldset disabled={busy || !expiresIn}>
            <div className="otpWrp">
              <OTPInput value={otp} onChange={setOtp} numInputs={6} inputType="tel"
                renderInput={(props, index) => <input {...props} inputMode="numeric" autoComplete="one-time-code" aria-label={`OTP digit ${index + 1}`} />} />
            </div>
          </fieldset>
          {error && <p className="error mt-2" role="alert">{error}</p>}
          {message && <p className="themeClr mt-2" role="status">{message}</p>}
          {/* <p className="text-muted mt-3" role={expiresIn ? undefined : "alert"}>
            {expiresIn ? `Code expires in ${Math.floor(expiresIn / 60)}:${String(expiresIn % 60).padStart(2, "0")}` : "This code has expired. Please log in again."}
          </p> */}
        </Col>
        <Col lg="12" className="text-center mt-3">
          <Button type="button" variant="link" style={{backgroundColor: "transparent"}} className="themeClr p-0" onClick={handleResend} disabled={busy || resendIn > 0 || !expiresIn}>
            {resending ? "Sending..." : resendIn > 0 ? `Resend OTP in ${resendIn}s` : "Resend OTP"}
          </Button>
        </Col>
        <Col lg="12" className="my-2">
          <div className={`${styles.btnWrpper} pt-3 btnWrpper`}>
            <LoadingButton type="submit" isLoading={verifying} disabled={busy || !expiresIn || !/^\d{6}$/.test(otp)} className="d-flex align-items-center justify-content-center w-100 commonBtn text-uppercase">
              Verify OTP
            </LoadingButton>
          </div>
        </Col>
        <Col lg="12" className="text-center my-2">
          <Button type="button" variant="link" className="themeClr" disabled={busy} onClick={() => onRestart()}>Back to login</Button>
        </Col>
      </Row>
    </Form>
  );
};

export default LoginOtp;
